import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { ArrowRight, Plus, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createDraft, getActionItems } from '../../api/claimsClient';

const BORDER = '1px solid rgba(0,0,0,0.15)';
const MUTED = '#64748B';
const HAIRLINE = '1px solid rgba(0,0,0,0.06)';
const DENIED_ACCENT = '#FF0004';
const PAID_ACCENT = '#1F8A4C';

const EMPTY_COUNTS = { draft: 0, denied_unappealed: 0, rejected: 0, total: 0 };
const EMPTY_DOLLARS = { denied_recoverable: '0', paid_optum_tracked: '0' };

const formatMoney = (value) => {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return '$0';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const OverviewPage = () => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState(null);
  const [dollars, setDollars] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getActionItems();
      setCounts(data?.counts || null);
      setDollars(data?.dollars || null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load action items.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const goToFiltered = (effectiveStatus) => {
    navigate(`/claims?effective_status=${effectiveStatus}`);
  };

  const goToClaims = () => navigate('/claims');

  const handleCreateClaim = async () => {
    setCreating(true);
    try {
      const fresh = await createDraft();
      navigate(`/claims/${fresh.id}`);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to create claim.');
    } finally {
      setCreating(false);
    }
  };

  const activeCounts = counts || EMPTY_COUNTS;
  const activeDollars = dollars || EMPTY_DOLLARS;

  return (
    <Box sx={{ px: { xs: 2, sm: '40px' }, pt: { xs: 3, sm: '36px' }, pb: 8 }}>
      <PageHeader
        onRefresh={load}
        refreshing={loading}
        onCreateClaim={handleCreateClaim}
        creating={creating}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 3 }}>
        {loading && !counts ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr 1fr' },
              gap: 2,
            }}
          >
            <ActionItemsHero
              counts={activeCounts}
              onGoToFiltered={goToFiltered}
              onOpenQueue={goToClaims}
            />
            <PaidCard amount={activeDollars.paid_optum_tracked} />
            <DeniedRecoverableCard
              amount={activeDollars.denied_recoverable}
              count={activeCounts.denied_unappealed}
              onClick={() => goToFiltered('denied')}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

function PageHeader({ onRefresh, refreshing, onCreateClaim, creating }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
      }}
    >
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: { xs: 24, sm: 32 }, color: 'black' }}>
          Overview
        </Typography>
        <Typography sx={{ fontSize: 14, color: MUTED, mt: 0.5 }}>
          Snapshot of your claims, denials, and payer outcomes
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshCw size={16} />}
          onClick={onRefresh}
          disabled={refreshing}
          sx={{ textTransform: 'none' }}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={onCreateClaim}
          disabled={creating}
          sx={{ textTransform: 'none' }}
        >
          Create Claim
        </Button>
      </Box>
    </Box>
  );
}

function ActionItemsHero({ counts, onGoToFiltered, onOpenQueue }) {
  const breakdown = [
    { count: counts.draft, label: 'drafts', filter: 'draft' },
    { count: counts.denied_unappealed, label: 'to appeal', filter: 'denied' },
    { count: counts.rejected, label: 'to rebuild', filter: 'rejected' },
  ];

  return (
    <Box
      sx={{
        border: BORDER,
        borderRadius: '4px',
        bgcolor: 'white',
        p: { xs: 3, sm: 4 },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 3, md: 4 },
        alignItems: { md: 'flex-start' },
        minHeight: 200,
      }}
    >
      <Box sx={{ flex: '0 0 auto', minWidth: 180 }}>
        <Eyebrow>Action items</Eyebrow>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: 48, sm: 56 },
            lineHeight: 0.95,
            letterSpacing: '-0.02em',
            color: 'black',
            mt: 1.5,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {counts.total}
        </Typography>
        <Button
          variant="text"
          onClick={onOpenQueue}
          sx={{
            mt: 2,
            p: 0,
            minWidth: 0,
            fontWeight: 600,
            textTransform: 'none',
            textDecoration: 'underline',
            color: 'black',
            '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
          }}
        >
          Open queue
        </Button>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {breakdown.map((item, idx) => (
          <BreakdownRow
            key={item.filter}
            count={item.count}
            label={item.label}
            first={idx === 0}
            onClick={() => onGoToFiltered(item.filter)}
          />
        ))}
      </Box>
    </Box>
  );
}

function PaidCard({ amount }) {
  return (
    <DollarCard accent={PAID_ACCENT}>
      <Eyebrow>Paid (Optum-tracked)</Eyebrow>
      <BigDollar value={amount} />
      <Typography sx={{ fontSize: 13, color: MUTED, mt: 1 }}>
        Reflects Optum-tracked claims only.
      </Typography>
    </DollarCard>
  );
}

function DeniedRecoverableCard({ amount, count, onClick }) {
  return (
    <DollarCard accent={DENIED_ACCENT}>
      <Eyebrow>Denied (recoverable)</Eyebrow>
      <BigDollar value={amount} />
      <Typography sx={{ fontSize: 13, color: MUTED, mt: 1 }}>
        {count} eligible for appeal
      </Typography>
      <Button
        variant="text"
        onClick={onClick}
        sx={{
          mt: 'auto',
          alignSelf: 'flex-start',
          p: 0,
          minWidth: 0,
          fontWeight: 600,
          textTransform: 'none',
          textDecoration: 'underline',
          color: 'black',
          '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
        }}
      >
        Review denials →
      </Button>
    </DollarCard>
  );
}

function DollarCard({ accent, children }) {
  return (
    <Box
      sx={{
        border: BORDER,
        borderLeft: `3px solid ${accent}`,
        borderRadius: '4px',
        bgcolor: 'white',
        p: { xs: 3, sm: 4 },
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minHeight: 200,
      }}
    >
      {children}
    </Box>
  );
}

function Eyebrow({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: MUTED,
      }}
    >
      {children}
    </Typography>
  );
}

function BigDollar({ value }) {
  return (
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: { xs: 36, sm: 44 },
        lineHeight: 1,
        letterSpacing: '-0.02em',
        color: 'black',
        mt: 1.5,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {formatMoney(value)}
    </Typography>
  );
}

function BreakdownRow({ count, label, first, onClick }) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 1.5,
        py: 1.5,
        borderTop: first ? 'none' : HAIRLINE,
        cursor: 'pointer',
        outline: 'none',
        '&:hover .breakdown-label': { textDecoration: 'underline' },
        '&:focus-visible': { backgroundColor: 'rgba(0,0,0,0.04)' },
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 24,
          minWidth: 48,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {count}
      </Typography>
      <Typography
        className="breakdown-label"
        sx={{ fontSize: 15, color: 'black', flex: 1 }}
      >
        {label}
      </Typography>
      <ArrowRight size={16} color={MUTED} />
    </Box>
  );
}

export default OverviewPage;
